import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { RemoveScroll } from "react-remove-scroll";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { iosSetBackgroundColor, isIOSApp } from "../../tools/ios";

/**
 * ONE duration and ONE curve for the whole gesture.
 *
 * The morph, the backdrop and the image crossfade all run on these, because the
 * darkening and the growth are a single gesture rather than a sequence. The
 * previous version had no backdrop animation at all — it hard-cut to 95% black
 * on the first frame, which is what buried the image.
 */
// Exported: the CARD's image shares them. It is the element that travels on the
// way back (motion promotes it), so if it animated on different timing the close
// would not be the reverse of the open — it would be a spring answering a tween.
export const MORPH_SECONDS = 0.32;
export const MORPH_EASE = [0.22, 1, 0.36, 1] as const;
/** The close affordance follows the content; it must never precede it. */
const CHROME_SECONDS = 0.16;
/**
 * Short and undelayed, so the chrome is never what keeps the overlay mounted.
 * AnimatePresence waits for the longest exit in the subtree, and an invisible
 * full-screen portal still receives pointer events — so a slow chrome exit is a
 * swallowed tap, not a cosmetic detail.
 */
const CHROME_EXIT_SECONDS = 0.1;

/**
 * The exit is NOT the enter played backwards, and the frame harness is what
 * proved it. On close, motion promotes the card and the card's image becomes the
 * element that travels — but it lives BEHIND this backdrop. With the backdrop
 * fading on the full morph duration, the travelling image stayed occluded and
 * six consecutive frames contained no image at all (the same occlusion that
 * caused the black open, arriving from the other side).
 *
 * So the backdrop clears FAST, uncovering the morph almost immediately, and the
 * overlay's own image fades slightly slower so it still covers the crossover
 * while the card's image becomes visible beneath it. The morph itself keeps the
 * full duration.
 */
const BACKDROP_EXIT_SECONDS = 0.08;
/**
 * Held fully opaque for the first half, then faded. A plain 1→0 fade left three
 * frames around the crossover where the overlay image was ~40% opaque, the
 * backdrop was not yet clear, and the card's morphing image underneath was still
 * too dark to count as present. Staying opaque until the backdrop has gone means
 * the handover happens between two things that are both visible.
 */
const IMAGE_EXIT_SECONDS = 0.34;
const IMAGE_EXIT_OPACITY = [1, 1, 0] as const;
// Frame capture put a number on the handover: the card's image does not start
// painting until ~160ms into the close, so this stays fully opaque until ~240ms
// and only then fades. Fading earlier left a real gap — six frames of an almost
// blank screen, which is the same "image is absent" failure the open had.
const IMAGE_EXIT_TIMES = [0, 0.7, 1] as const;
/** No morph is visible when the card is gone, so the image must not vanish abruptly. */
const IMAGE_EXIT_IN_PLACE_SECONDS = 0.28;

/**
 * Full-screen viewer for an IMAGE congress document.
 *
 * THE RULE: the image is never absent. The same decoded pixels stay on screen
 * continuously from card to lightbox — it grows, it does not disappear and
 * reappear.
 *
 * WHICH CHANGE CARRIES THAT, measured rather than assumed. Each was reverted
 * against the frame harness (`playwright/lightbox-frames.spec.ts`), and the
 * results contradicted the plan they were written from:
 *
 *  1. OPENING ON THE CARD'S OWN RENDITION carries the OPEN. The overlay paints a
 *     decoded bitmap on frame 1, above the backdrop, so the open stays
 *     frame-continuous even with the backdrop reverted to its old hard cut. This
 *     was expected to be a mere optimisation; it is the load-bearing change.
 *  2. THE BACKDROP'S FAST EXIT carries the CLOSE. Reverting it costs 15 imageless
 *     frames — on closing, not opening. Motion promotes the card and the CARD's
 *     image becomes the element that travels, and it lives BEHIND this backdrop,
 *     so a backdrop that does not clear quickly hides the whole reverse morph.
 *     (The fade-in still matters for how the open READS — the feed stays visible
 *     behind the growing image — but not for frame continuity.)
 *  3. THE EXIT PROPS carry the hold. Without them `AnimatePresence` unmounts on
 *     the next frame: 19 imageless frames.
 *  4. THE RESERVED ASPECT RATIO is unverified, and unverifiABLE while (1) stands.
 *     No mutation could turn the harness red — including under 20x CPU throttling
 *     — and the reason is not that it does nothing: it is that (1) makes its
 *     condition unreachable. The two are COUPLED. Keeping it is what stops the
 *     system from becoming permanently dependent on the lightbox opening with the
 *     card's own src, with nothing to catch it if someone later changes that for a
 *     good reason. The underlying fact is not speculative either — `height: auto`
 *     on an undecoded `<img>` resolves to zero, by spec. So: defence whose value
 *     is conditional on another fix holding. Do not "clean it up" because no test
 *     covers it; no test CAN cover it until (1) changes, and if (1) ever changes
 *     this is what keeps the box correct.
 *
 * `centerOnInit` is gone regardless: it centred on a MEASURED size that was zero
 * until decode, which is why the image appeared low and then jumped up.
 *
 * The original is swapped in only after the morph settles, decoded off-screen
 * first so there is no reflow and no visible re-decode.
 *
 * OPENS FIT-TO-WIDTH: a landscape floor plan spans the viewport with black above
 * and below. Derived from the aspect ratio, never measured.
 *
 * An OVERLAY, not a route — it never navigates, so it cannot land in the native
 * shells' chrome-less webview.
 */
export function CongressImageLightbox({
  cardSrc,
  originalSrc,
  aspectRatio,
  alt,
  layoutId,
  exitInPlace,
  onClose,
}: {
  /** The rendition the card is ALREADY showing — decoded, so the morph never waits. */
  cardSrc: string;
  /** Swapped in after the morph settles, for zoom sharpness. */
  originalSrc: string;
  /** Intrinsic ratio, so the box is right before the first frame. Null = unknown. */
  aspectRatio: number | null;
  alt: string;
  /** Shared with the card, so motion morphs the box geometry between them. */
  layoutId: string;
  /**
   * True when the source card is not on screen.
   *
   * PRECISELY WHAT THIS DOES, because the earlier wording overstated it: the
   * reverse morph STILL RUNS. `layoutId` stays on both elements, so motion still
   * promotes the card and animates it to its box. What changes is only this
   * overlay image's opacity exit — a plain, slightly longer fade instead of the
   * hold-then-handover used when the card is visible.
   *
   * That is sufficient, and only because the box is FULLY outside the viewport
   * (`bottom > 0 && top < innerHeight` is what decides it), so the element being
   * morphed is never painted and the reader sees a fade in place. It is not a
   * suppressed morph; it is an invisible one. Suppressing it properly would mean
   * dropping `layoutId` a frame before unmount, which promotes the card WHILE the
   * overlay is still visible and glitches — so this is the deliberate trade.
   */
  exitInPlace: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [src, setSrc] = useState(cardSrc);

  // The iOS shell paints outside the webview, so a dark overlay shows a white
  // band unless the shell is told.
  //
  // SET AND RESTORE BOTH LIVE HERE, in the effect, and that is now the correct
  // place precisely BECAUSE the exit is held. Cleanup used to fire at close
  // initiation — the overlay unmounted on the next frame — which flashed white
  // mid-morph, so an earlier revision moved the restore to the host's
  // `onExitComplete`. That traded one bug for a worse one: `onExitComplete` never
  // fires if the host itself unmounts while the overlay is open (navigating away,
  // or a refetch dropping the block), leaving the shell BLACK on the next screen.
  // Both bots found that independently.
  //
  // With `exit` props in place, AnimatePresence keeps this component mounted until
  // the exit finishes, so cleanup runs after it — the moment we wanted — and it
  // still runs on an abrupt unmount, which is the case `onExitComplete` cannot
  // cover. One path, both cases.
  useEffect(() => {
    if (!isIOSApp()) return undefined;
    iosSetBackgroundColor("#000000");
    return () => {
      iosSetBackgroundColor("#FFFFFF");
    };
  }, []);

  /**
   * Upgrade to the original, invisibly. Decoded BEFORE the swap so the paint is
   * atomic, and into the same box so there is no reflow.
   *
   * Failure is deliberately silent and terminal: we keep the card rendition
   * forever. Soft under heavy zoom, never blank — the same degraded-not-broken
   * posture as a null `sizes`. There is no error state worth showing a reader.
   */
  const upgradeToOriginal = () => {
    if (originalSrc === cardSrc) return;
    const preload = new Image();
    preload.src = originalSrc;
    void preload
      .decode()
      .then(() => {
        setSrc(originalSrc);
      })
      .catch(() => undefined);
  };

  return createPortal(
    <RemoveScroll>
      <motion.div
        className="fixed inset-0 z-900 bg-black/95 backdrop-blur-xs"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{
          opacity: 0,
          transition: { duration: BACKDROP_EXIT_SECONDS, ease: "linear" },
        }}
        transition={{ duration: MORPH_SECONDS, ease: MORPH_EASE }}
      >
        {/* Delayed in, faded out fast. A close button on a black screen with no
            image was the tell that chrome mounted ahead of content. */}

        {/* SEPARATE CONCERN, deliberately its own block: the chip's CONTRAST. The
            comment above is about WHEN this button appears; this is about whether
            it can be SEEN. They were one block and that is how a comment stops
            being read — whoever comes to edit the delay had to read contrast first.

            THE CHIP IS DARK, AND THAT IS THE WHOLE POINT. It used to be
            `bg-white/15` — a translucent LIGHT chip under a white glyph, styled
            against the black/95 backdrop. But the backdrop is only visible where
            the image is not: the image fits to WIDTH, so any view state where its
            rendered height exceeds the viewport puts paper in this corner, and a
            white glyph on a 15%-white chip over white paper measures 255/255/255.
            The chip was making it worse, by lightening the local background.

            This is NOT aspect-gated and NOT desktop-only. MEASURED: a letterboxed
            0.94 plan at 390x844 — a configuration that is SAFE at rest — reads
            123/123/123 in the button rect at rest and 255/255/255 after a pinch.
            So a zoom alone loses the glyph on a case the at-rest arithmetic clears.
            DERIVED from the transform semantics, not separately measured: the
            rendered height is `scale × viewportWidth / aspect`, which makes the
            covering condition a property of the VIEW STATE rather than of the
            image, so it is reachable beyond the one case above. (Panning is bounded
            — `limitToBounds` is at its default — so a user cannot pan the image
            fully out of view.)

            THE NUMBER THE HARNESS LOGS IS 7.95, not the 8.5 this arithmetic
            predicts (0.7-alpha black over white paper gives a chip of 76.5, glyph
            255 → 8.54). The gap is real and benign: the glyph class picks up
            antialiased edge pixels that pull its mean below pure white, and the
            chip class picks up the `ring-white/25` and sub-threshold edge pixels
            that pull its mean up. Both narrow the ratio. Expect 7.95; if you see
            8.5 the classifier changed. Over a dark image it is ~21:1.

            The colour is deliberately NOT derived from the image — that would need
            re-measuring on every zoom, pan and crop, and fails open exactly where
            the content is ambiguous. */}
        <motion.button
          type="button"
          onClick={onClose}
          // The ACTION, not the document. This was `alt` — the card title — so a
          // screen reader announced "Where to find us, button" for a dismiss
          // control. `t()` resolves to `activeLang`, the INTERFACE locale (it is
          // what `changeActiveLang` passes to `i18n.changeLanguage`), which is the
          // correct axis: the X is overlay chrome. The document beneath follows
          // `contentLang`, a different axis, and the title stays on the image's alt.
          aria-label={t("Close")}
          className="absolute top-(--safe-area-top) right-4 z-10 mt-3 flex size-10 cursor-pointer items-center justify-center rounded-full bg-black/70 text-white ring-1 ring-white/25"
          initial={{ opacity: 0 }}
          // THE DELAY LIVES ON `animate`, NOT ON A SHARED `transition`.
          //
          // It exists to hold the chrome back on the way IN. Put on the top-level
          // `transition` it silently applied to the way OUT too, because a
          // top-level transition covers every target the element animates unless
          // that target overrides it — and `exit` did not. The exit therefore took
          // 0.32s delay + 0.16s, AnimatePresence waits for the LONGEST exit, and
          // the full-screen portal stayed mounted 409ms after the backdrop had
          // reached opacity 0, with `pointer-events: auto`, swallowing the first
          // tap and holding RemoveScroll's lock. (Measured, not inferred: opacity
          // 0 at 89ms, unmount at 498ms.)
          //
          // Keeping it on the enter target means no exit can inherit it, including
          // one added later by someone who never reads this. That is the fix — the
          // explicit exit transition below is for determinism, not the repair.
          animate={{
            opacity: 1,
            transition: {
              duration: CHROME_SECONDS,
              delay: MORPH_SECONDS,
              ease: "linear",
            },
          }}
          exit={{
            opacity: 0,
            transition: { duration: CHROME_EXIT_SECONDS, ease: "linear" },
          }}
        >
          <IconX className="size-6 stroke-[1.5px]" />
        </motion.button>
        <TransformWrapper
          initialScale={1}
          minScale={1}
          maxScale={6}
          disablePadding
        >
          <TransformComponent
            wrapperStyle={{ width: "100vw", height: "100dvh" }}
            // Vertically centred by flex, not by measuring the content — which is
            // what `centerOnInit` did, on a size that was zero until decode.
            contentStyle={{
              width: "100vw",
              height: "100dvh",
              display: "flex",
              alignItems: "center",
            }}
          >
            {/* `w-full` plus a known `aspect-ratio` IS the fit-to-width rule, and
                it gives the morph a correct target before anything is decoded.
                With no ratio available this falls back to `h-auto` — the old
                behaviour, and the old jump — which requires `sizes` to be null
                AND the card image to be undecoded, and the reader just tapped it. */}
            <motion.img
              layoutId={layoutId}
              src={src}
              alt={alt}
              className={aspectRatio === null ? "h-auto w-full" : "w-full"}
              style={{
                aspectRatio: aspectRatio === null ? undefined : aspectRatio,
              }}
              exit={{
                opacity: exitInPlace ? 0 : [...IMAGE_EXIT_OPACITY],
                transition: exitInPlace
                  ? { duration: IMAGE_EXIT_IN_PLACE_SECONDS, ease: "linear" }
                  : {
                      duration: IMAGE_EXIT_SECONDS,
                      times: [...IMAGE_EXIT_TIMES],
                      ease: "linear",
                    },
              }}
              transition={{
                duration: MORPH_SECONDS,
                ease: MORPH_EASE,
                layout: { duration: MORPH_SECONDS, ease: MORPH_EASE },
              }}
              onLayoutAnimationComplete={upgradeToOriginal}
            />
          </TransformComponent>
        </TransformWrapper>
      </motion.div>
    </RemoveScroll>,
    document.body
  );
}

/**
 * Holds the overlay until the exit finishes.
 *
 * Without an `exit` prop anywhere in the subtree, `AnimatePresence` has nothing
 * to await and unmounts on the next frame — which is what made closing abrupt.
 * The backdrop's exit is what keeps the overlay mounted; the reverse morph runs
 * CONCURRENTLY with it, because motion calls `relegate()` when presence flips,
 * not when the element unmounts (framer-motion's MeasureLayout, and its own
 * comment: the surviving member "is in charge of the exit animation"), so the
 * card is promoted and starts morphing at close initiation.
 */
export function CongressImageLightboxHost({
  open,
  cardSrc,
  originalSrc,
  aspectRatio,
  alt,
  layoutId,
  exitInPlace,
  onClose,
}: {
  open: boolean;
  cardSrc: string | null;
  originalSrc: string | null;
  aspectRatio: number | null;
  alt: string;
  layoutId: string;
  exitInPlace: boolean;
  onClose: () => void;
}): React.JSX.Element {
  return (
    <AnimatePresence>
      {open && cardSrc !== null && originalSrc !== null && (
        <CongressImageLightbox
          cardSrc={cardSrc}
          originalSrc={originalSrc}
          aspectRatio={aspectRatio}
          alt={alt}
          layoutId={layoutId}
          exitInPlace={exitInPlace}
          onClose={onClose}
        />
      )}
    </AnimatePresence>
  );
}
